import { useState } from "react";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import SystemValueForm from "../../../../components/SystemValue/SystemValueForm";
import { formatTaxValue, taxTypeLabel } from "./taxFormat";
const validateField = (name, form, tax) => {
  const value = form[name];
  if (name === "tax_id" && !value) return "Tax is required.";
  if (name === "value" && tax?.type === "absolute") {
    if (value === "") return "Default Value is required.";
    const n = Number(value);
    if (Number.isNaN(n)) return "Default Value must be a number.";
    if (n < 0) return "Default Value cannot be negative.";
  }
  return "";
};

const FIELDS = ["tax_id", "value"];

const DefaultTaxForm = ({ party, taxToEdit, taxes, usedTaxIds, onSave, onCancel }) => {
  const [form, setForm] = useState(() => ({
    tax_id: taxToEdit?.tax_id ? String(taxToEdit.tax_id) : "",
    value: taxToEdit?.type === "absolute" ? String(Number(taxToEdit.value)) : "",
  }));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const taxFor = (id) => taxes.find((t) => String(t.id) === String(id));
  const selected = taxFor(form.tax_id);
  const absolute = selected?.type === "absolute";

  const taxOptions = taxes.map((t) => ({
    value: String(t.id),
    label: `${t.name} (${taxTypeLabel(t.type)})`,
    disabled: usedTaxIds.has(t.id) && t.id !== taxToEdit?.tax_id,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...form, [name]: value };
    if (name === "tax_id" && taxFor(value)?.type !== "absolute") next.value = "";
    setForm(next);
    setErrors((prev) => ({ ...prev, [name]: "", ...(name === "tax_id" ? { value: "" } : {}) }));
    setFormError("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, { ...form, [name]: value }, selected) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = Object.fromEntries(FIELDS.map((f) => [f, validateField(f, form, selected)]));
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSubmitting(true);
    const failure = await onSave({
      tax_id: Number(form.tax_id),
      ...(absolute ? { value: Number(form.value) } : {}),
    });
    setSubmitting(false);

    if (failure?.field) setErrors((prev) => ({ ...prev, [failure.field]: failure.message }));
    else if (failure) setFormError(failure.message);
  };

  return (
    <SystemValueForm
      title={taxToEdit ? "Edit Default Tax" : "Add Default Tax"}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      error={formError}
      submitting={submitting}
      submitLabel={taxToEdit ? "Update" : "Add"}
    >
      <FormSelect
        label="Tax"
        name="tax_id"
        value={form.tax_id}
        onChange={handleChange}
        onBlur={handleBlur}
        options={taxOptions}
        error={errors.tax_id}
        placeholder={taxes.length ? "Select Tax" : "No taxes defined in Settings > Taxes"}
        required
      />
      {selected?.type === "percentage" && (
        <p className="text-xs text-gray-500">
          Applied at {formatTaxValue(selected)}, the value set for this tax in Settings &gt; Taxes.
        </p>
      )}
      {absolute && (
        <>
          <FormInput
            label="Default Value"
            name="value"
            type="number"
            value={form.value}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.value}
            placeholder="e.g. 50.00"
            required
          />
          <p className="text-xs text-gray-500">Charged in each {party}'s default currency.</p>
        </>
      )}
    </SystemValueForm>
  );
};

export default DefaultTaxForm;